import { useState } from 'react';
import { parseDailyReport, submitAvomanDailyUpdate } from '../lib/admin-daily-report-api';
import { useAdminAuthStore } from '../stores/useAdminAuthStore';
import './DailyReportPage.css';

interface StationForecastRow {
  town: string;
  weather: string;
  low: string;
  high: string;
  windDirection: string;
  windLevel: string;
}

interface GeneratedReport {
  sms: string;
  avoman: string;
  wordHtml: string;
  previewPngUrl: string;
  fileBaseName: string;
}

const DEFAULT_STATION_ROWS = `乡镇,24小时天气,最低气温,最高气温,风向,风力（级）
娜允,晴间多云,19,32,西风,2
景信,晴间多云,19,32,西风,2
富岩,晴间多云,18,28,西风,2
公信,晴间多云,17,30,西风,2
勐马,晴间多云,17,30,西风,2
芒信,晴间多云,18,33,西北风,2`;

const DEFAULT_ADVICE = `1.病害防控：田间枯萎病发生严重，请做好相关防控工作；幼果膨大期重点防控急性炭疽病和锈病，轮换使用药剂；同时注意预防疫霉根腐病，避免树盘积水。
2.保果管理：果实进入快速发育期，保果重点转向营养稳果与控梢保果，补充树体营养，促进幼果膨大、减少裂果；及时控梢，减少养分竞争，防止二次落果。
3.苗期管护：嫁接苗及时绑杆固定，防止风折；及时完全解除绑膜，避免缢伤；薄施平衡型水溶肥促进根系生长，并预防炭疽病及蚜虫、潜叶蛾。`;

const DEFAULT_UNITS = `孟连县气象局
孟连县牛油果科学技术研究中心`;

const DEFAULT_ATTACHMENT_CAPTION = '以上是明天的天气预报和农事建议，看完你有什么问题，需要我帮你解答一下吗？';

function todayInputValue(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function chineseDate(dateValue: string): string {
  const [year, month, day] = dateValue.split('-').map((part) => Number(part));
  if (!year || !month || !day) return dateValue;
  return `${year}年${month}月${day}日`;
}

function parseStationRows(value: string): StationForecastRow[] {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line, index) => !(index === 0 && line.includes('乡镇')))
    .map((line) => line.split(',').map((cell) => cell.trim()))
    .filter((cells) => cells.length >= 6)
    .map(([town, weather, low, high, windDirection, windLevel]) => ({
      town,
      weather,
      low,
      high,
      windDirection,
      windLevel,
    }));
}

function splitAdviceItems(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function formatStationForecasts(rows: StationForecastRow[]): string[] {
  return rows.map((row) => {
    const temperature = `${row.low}-${row.high}℃`;
    const wind = `${row.windDirection}${row.windLevel}级`;
    return `${row.town}：${row.weather}，${temperature}，${wind}`;
  });
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const result: string[] = [];
  let current = '';
  Array.from(text).forEach((char) => {
    const next = current + char;
    if (ctx.measureText(next).width > maxWidth && current) {
      result.push(current);
      current = char;
      return;
    }
    current = next;
  });
  if (current) result.push(current);
  return result;
}

function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): number {
  const lines = wrapText(ctx, text, maxWidth);
  lines.forEach((line, index) => ctx.fillText(line, x, y + index * lineHeight));
  return y + lines.length * lineHeight;
}

function buildSmsReport(input: {
  issueNumber: string;
  forecastText: string;
  adviceText: string;
  disasterTip: string;
  units: string;
  reportDate: string;
}): string {
  return [
    `牛油果专题天气（2026年第${input.issueNumber}期）`,
    '天气实况与预报：',
    input.forecastText,
    '农事建议：',
    input.adviceText,
    '气象灾害提示：',
    input.disasterTip,
    '祝丰产！',
    '播报单位：',
    input.units,
    chineseDate(input.reportDate),
  ].join('\n');
}

function buildAvomanReport(input: {
  forecastText: string;
  stationRows: StationForecastRow[];
  adviceItems: string[];
  disasterTip: string;
  units: string;
  reportDate: string;
}): string {
  return [
    '大家好，我是牛博士。明天牛油果园区管理，先看天气和田间风险。',
    '',
    `天气上：${input.forecastText}`,
    ...formatStationForecasts(input.stationRows).map((line) => `- ${line}`),
    '',
    '明天请重点抓三件事：',
    ...input.adviceItems.map((item) => `- ${item}`),
    '',
    `另外提醒：${input.disasterTip}`,
    '',
    '把病害、保果和苗期管护提前做在前面，稳住树势，也稳住产量。',
    '',
    input.units,
    chineseDate(input.reportDate),
  ].join('\n');
}

function buildWordHtml(input: {
  issueNumber: string;
  publishTime: string;
  forecastText: string;
  adviceItems: string[];
  disasterTip: string;
  stationRows: StationForecastRow[];
}): string {
  const stationRowsHtml = input.stationRows
    .map(
      (row) => `<tr><td>${escapeHtml(row.town)}</td><td>${escapeHtml(row.weather)}</td><td>${escapeHtml(row.low)}</td><td>${escapeHtml(row.high)}</td><td>${escapeHtml(row.windDirection)}</td><td>${escapeHtml(row.windLevel)}</td></tr>`,
    )
    .join('');
  const adviceHtml = input.adviceItems
    .map((item) => `<p>${escapeHtml(item)}</p>`)
    .join('');

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>孟连牛油果未来24小时专项天气预报2026年第${escapeHtml(input.issueNumber)}期</title>
<style>
@page { size: A4; margin: 22mm 24mm; }
body { color: #000; font-family: SimSun, Songti SC, serif; font-size: 16px; line-height: 1.8; }
.report { width: 680px; margin: 0 auto; }
.masthead { color: #e60000; text-align: center; font-size: 42px; font-weight: 700; letter-spacing: 2px; }
.issue { text-align: center; margin: 8px 0 18px; }
.meta { display: flex; justify-content: space-between; border-bottom: 3px solid #e60000; padding-bottom: 3px; font-size: 14px; }
h1 { margin: 28px 0 20px; text-align: center; font-size: 24px; }
h2 { margin: 22px 0 8px; font-size: 18px; font-weight: 700; }
table { width: 100%; border-collapse: collapse; margin: 12px 0 20px; font-size: 14px; }
th, td { border: 1px solid #000; padding: 6px 8px; text-align: center; }
p { margin: 8px 0; text-indent: 2em; }
.tip { margin-top: 14px; }
</style>
</head>
<body>
<div class="report">
  <div class="masthead">“孟连牛油果”天气预报</div>
  <div class="issue">第 ${escapeHtml(input.issueNumber)} 期</div>
  <div class="meta"><span>孟连县气象台　孟连县牛油果科学技术研究中心</span><span>${escapeHtml(input.publishTime)} 发布</span></div>
  <h1>“孟连牛油果”未来24小时专项天气预报</h1>
  <h2>一、孟连县未来 24 小时天气预报</h2>
  <p>${escapeHtml(input.forecastText)}</p>
  <table>
    <thead><tr><th>乡镇</th><th>24小时天气</th><th>最低气温</th><th>最高气温</th><th>风向</th><th>风力（级）</th></tr></thead>
    <tbody>${stationRowsHtml}</tbody>
  </table>
  <h2>二、关注与建议</h2>
  ${adviceHtml}
  <p class="tip">气象灾害提示：${escapeHtml(input.disasterTip)}</p>
</div>
</body>
</html>`;
}

function buildPreviewPngUrl(input: {
  issueNumber: string;
  publishTime: string;
  forecastText: string;
  adviceItems: string[];
  disasterTip: string;
  stationRows: StationForecastRow[];
}): string {
  const canvas = document.createElement('canvas');
  const width = 920;
  canvas.width = width;
  canvas.height = 1320;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, width, canvas.height);
  ctx.textBaseline = 'top';

  ctx.fillStyle = '#e60000';
  ctx.font = '700 44px SimSun, serif';
  ctx.textAlign = 'center';
  ctx.fillText('“孟连牛油果”天气预报', width / 2, 22);

  ctx.fillStyle = '#000';
  ctx.font = '18px SimSun, serif';
  ctx.fillText(`第 ${input.issueNumber} 期`, width / 2, 88);

  ctx.textAlign = 'left';
  ctx.font = '16px SimSun, serif';
  ctx.fillText('孟连县气象台　孟连县牛油果科学技术研究中心', 70, 132);
  ctx.textAlign = 'right';
  ctx.fillText(`${input.publishTime} 发布`, width - 70, 132);
  ctx.strokeStyle = '#e60000';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(60, 160);
  ctx.lineTo(width - 60, 160);
  ctx.stroke();

  ctx.fillStyle = '#000';
  ctx.textAlign = 'center';
  ctx.font = '700 28px SimSun, serif';
  ctx.fillText('“孟连牛油果”未来24小时专项天气预报', width / 2, 202);

  ctx.textAlign = 'left';
  ctx.font = '700 22px SimSun, serif';
  ctx.fillText('一、孟连县未来 24 小时天气预报', 110, 270);
  ctx.font = '20px SimSun, serif';
  const forecastBottom = drawWrappedText(ctx, input.forecastText, 110, 314, 700, 32);

  const tableX = 90;
  let y = forecastBottom + 16;
  const colWidths = [110, 170, 120, 120, 120, 100];
  const rowHeight = 44;
  const headers = ['乡镇', '24小时天气', '最低气温', '最高气温', '风向', '风力（级）'];
  ctx.font = '18px SimSun, serif';
  ctx.textAlign = 'center';
  const drawRow = (cells: string[], rowY: number) => {
    let x = tableX;
    cells.forEach((cell, index) => {
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.strokeRect(x, rowY, colWidths[index], rowHeight);
      ctx.fillText(cell, x + colWidths[index] / 2, rowY + 11);
      x += colWidths[index];
    });
  };
  drawRow(headers, y);
  y += rowHeight;
  input.stationRows.slice(0, 8).forEach((row) => {
    drawRow([row.town, row.weather, row.low, row.high, row.windDirection, row.windLevel], y);
    y += rowHeight;
  });

  ctx.textAlign = 'left';
  y += 34;
  ctx.font = '700 22px SimSun, serif';
  ctx.fillText('二、关注与建议', 110, y);
  y += 44;
  ctx.font = '20px SimSun, serif';
  input.adviceItems.forEach((item) => {
    y = drawWrappedText(ctx, item, 110, y, 700, 32) + 8;
  });
  y += 6;
  ctx.fillText('气象灾害提示：', 110, y);
  y = drawWrappedText(ctx, input.disasterTip, 250, y, 560, 32);

  return canvas.toDataURL('image/png');
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function composeGeneratedReport(input: {
  issueNumber: string;
  reportDate: string;
  publishTime: string;
  forecastText: string;
  stationRowsText: string;
  adviceText: string;
  disasterTip: string;
  units: string;
}): GeneratedReport {
  const stationRows = parseStationRows(input.stationRowsText);
  const adviceItems = splitAdviceItems(input.adviceText);
  const fileBaseName = `孟连牛油果未来24小时专项天气预报2026年第${input.issueNumber}期`;
  const sms = buildSmsReport({
    issueNumber: input.issueNumber,
    forecastText: input.forecastText,
    adviceText: input.adviceText,
    disasterTip: input.disasterTip,
    units: input.units,
    reportDate: input.reportDate,
  });
  const avoman = buildAvomanReport({
    forecastText: input.forecastText,
    stationRows,
    adviceItems,
    disasterTip: input.disasterTip,
    units: input.units,
    reportDate: input.reportDate,
  });
  const wordHtml = buildWordHtml({
    issueNumber: input.issueNumber,
    publishTime: input.publishTime,
    forecastText: input.forecastText,
    adviceItems,
    disasterTip: input.disasterTip,
    stationRows,
  });
  const previewPngUrl = buildPreviewPngUrl({
    issueNumber: input.issueNumber,
    publishTime: input.publishTime,
    forecastText: input.forecastText,
    adviceItems,
    disasterTip: input.disasterTip,
    stationRows,
  });
  return { sms, avoman, wordHtml, previewPngUrl, fileBaseName };
}

export default function DailyReportPage() {
  const accessToken = useAdminAuthStore((state) => state.accessToken);
  const [issueNumber, setIssueNumber] = useState('136');
  const [reportDate, setReportDate] = useState(todayInputValue());
  const [publishTime, setPublishTime] = useState('2026年5月16日17时');
  const [forecastText, setForecastText] = useState('预计孟连县5月17日晴间多云，气温18-35。');
  const [stationRowsText, setStationRowsText] = useState(DEFAULT_STATION_ROWS);
  const [adviceText, setAdviceText] = useState(DEFAULT_ADVICE);
  const [disasterTip, setDisasterTip] = useState('注意防范雷电、短时强降水等强对流天气。');
  const [units, setUnits] = useState(DEFAULT_UNITS);
  const [generated, setGenerated] = useState<GeneratedReport | null>(null);
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [attachmentCaption, setAttachmentCaption] = useState(DEFAULT_ATTACHMENT_CAPTION);
  const [isParsingSource, setIsParsingSource] = useState(false);
  const [parseMessage, setParseMessage] = useState('');
  const [parseError, setParseError] = useState('');
  const [isSubmittingDailyUpdate, setIsSubmittingDailyUpdate] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');

  const generate = () => {
    setGenerated(composeGeneratedReport({
      issueNumber,
      reportDate,
      publishTime,
      forecastText,
      stationRowsText,
      adviceText,
      disasterTip,
      units,
    }));
  };

  const copyText = async (text: string) => {
    await navigator.clipboard.writeText(text);
  };

  const parseSourceFile = async () => {
    if (!sourceFile) {
      setParseError('请先选择一份 Word 文件。');
      return;
    }

    setIsParsingSource(true);
    setParseMessage('');
    setParseError('');

    try {
      const parsed = await parseDailyReport({
        file: sourceFile,
        caption: attachmentCaption.trim() || DEFAULT_ATTACHMENT_CAPTION,
      });

      const nextIssueNumber = parsed.issue_no?.trim() || issueNumber;
      const nextPublishTime = parsed.publish_time?.trim() || publishTime;
      const nextForecastText = parsed.weather_text.trim() || forecastText;
      const nextStationRowsText = parsed.station_rows_text?.trim() || stationRowsText;
      const nextAdviceText = parsed.advice_text.trim() || adviceText;
      const nextDisasterTip = parsed.hazard_text.trim() || disasterTip;
      const nextUnits = parsed.units_text?.trim() || units;

      setIssueNumber(nextIssueNumber);
      setPublishTime(nextPublishTime);
      setForecastText(nextForecastText);
      setStationRowsText(nextStationRowsText);
      setAdviceText(nextAdviceText);
      setDisasterTip(nextDisasterTip);
      setUnits(nextUnits);
      setGenerated(composeGeneratedReport({
        issueNumber: nextIssueNumber,
        reportDate,
        publishTime: nextPublishTime,
        forecastText: nextForecastText,
        stationRowsText: nextStationRowsText,
        adviceText: nextAdviceText,
        disasterTip: nextDisasterTip,
        units: nextUnits,
      }));

      const warnings = parsed.raw_warnings.length ? `；${parsed.raw_warnings.join('；')}` : '';
      setParseMessage(`已识别并回填：${parsed.source_file}${warnings}`);
    } catch (error) {
      setParseError(error instanceof Error ? error.message : 'Word 文件识别失败，请检查文件后重试。');
    } finally {
      setIsParsingSource(false);
    }
  };

  const submitDailyUpdate = async () => {
    if (!accessToken) {
      setSubmitError('管理员登录已失效，请重新登录。');
      return;
    }
    if (!generated?.avoman.trim()) {
      setSubmitError('请先生成牛博士口吻的推送内容。');
      return;
    }

    setIsSubmittingDailyUpdate(true);
    setSubmitMessage('');
    setSubmitError('');
    try {
      const captionText = attachmentCaption.trim() || DEFAULT_ATTACHMENT_CAPTION;
      const response = await submitAvomanDailyUpdate(accessToken, {
        weatherText: generated.avoman,
        captionText,
        sourceFile: sourceFile?.name ?? null,
      });
      setSubmitMessage(`已提交为下次定时推送内容：天气预报 1 条，附带一句话 1 条；更新 ${response.updated_count} 个活跃牛博士订阅。`);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : '提交失败，请稍后重试。');
    } finally {
      setIsSubmittingDailyUpdate(false);
    }
  };

  return (
    <main className="daily-report-page">
      <section className="daily-report-hero">
        <div>
          <p className="daily-report-eyebrow">MENGLIAN AVOCADO WEATHER DESK</p>
          <h1>孟连县牛油果科研中心日报生成器</h1>
          <p>
            输入当天气象信息和关注建议，一键生成短信推送、Word 版专项天气预报、预览图片和牛博士口吻推送文案。
          </p>
        </div>
        <button className="daily-report-primary" type="button" onClick={generate}>
          确认并生成
        </button>
      </section>

      <section className="daily-report-grid">
        <div className="daily-report-card daily-report-form">
          <div className="daily-report-inline">
            <label>
              期号
              <input value={issueNumber} onChange={(event) => setIssueNumber(event.target.value)} />
            </label>
            <label>
              报告日期
              <input type="date" value={reportDate} onChange={(event) => setReportDate(event.target.value)} />
            </label>
            <label>
              发布时间
              <input value={publishTime} onChange={(event) => setPublishTime(event.target.value)} />
            </label>
          </div>

          <section className="daily-report-source-panel" aria-label="Word 文件识别">
            <div>
              <h2>Word 文件识别</h2>
              <p>上传标准天气预报 Word 文件，系统会把识别出的天气、农事建议和灾害提示回填到下方表单。</p>
            </div>
            <label>
              Word 文件
              <input
                accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                type="file"
                onChange={(event) => {
                  setSourceFile(event.target.files?.[0] ?? null);
                  setParseMessage('');
                  setParseError('');
                }}
              />
            </label>
            <label>
              附带一句话
              <textarea
                value={attachmentCaption}
                onChange={(event) => setAttachmentCaption(event.target.value)}
                rows={3}
              />
            </label>
            <div className="daily-report-actions">
              <button
                className="daily-report-secondary"
                type="button"
                onClick={parseSourceFile}
                disabled={isParsingSource}
              >
                {isParsingSource ? '识别中...' : '提交文件并识别'}
              </button>
              {sourceFile ? <span>{sourceFile.name}</span> : null}
            </div>
            {parseMessage ? <p className="daily-report-status">{parseMessage}</p> : null}
            {parseError ? <p className="daily-report-error">{parseError}</p> : null}
          </section>

          <label>
            天气实况与预报
            <textarea value={forecastText} onChange={(event) => setForecastText(event.target.value)} rows={3} />
          </label>

          <label>
            乡镇预报表格（CSV，每行：乡镇,天气,最低,最高,风向,风力）
            <textarea value={stationRowsText} onChange={(event) => setStationRowsText(event.target.value)} rows={8} />
          </label>

          <label>
            关注与建议
            <textarea value={adviceText} onChange={(event) => setAdviceText(event.target.value)} rows={9} />
          </label>

          <label>
            气象灾害提示
            <textarea value={disasterTip} onChange={(event) => setDisasterTip(event.target.value)} rows={2} />
          </label>

          <label>
            播报单位
            <textarea value={units} onChange={(event) => setUnits(event.target.value)} rows={2} />
          </label>
        </div>

        <div className="daily-report-card daily-report-preview">
          <h2>Word 预览</h2>
          {generated?.previewPngUrl ? (
            <img src={generated.previewPngUrl} alt="孟连牛油果天气预报预览" />
          ) : (
            <div className="daily-report-empty">点击“确认并生成”后显示预览图</div>
          )}
        </div>
      </section>

      {generated && (
        <section className="daily-report-results">
          <article className="daily-report-card daily-report-output">
            <div className="daily-report-output-head">
              <h2>短信形式的明日推送消息</h2>
              <button type="button" onClick={() => copyText(generated.sms)}>复制</button>
            </div>
            <pre>{generated.sms}</pre>
          </article>

          <article className="daily-report-card daily-report-output">
            <div className="daily-report-output-head">
              <h2>Word 文件与预览截图</h2>
              <div>
                <button
                  type="button"
                  onClick={() =>
                    downloadBlob(
                      new Blob([generated.wordHtml], { type: 'application/msword;charset=utf-8' }),
                      `${generated.fileBaseName}.doc`,
                    )
                  }
                >
                  下载 Word
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = generated.previewPngUrl;
                    link.download = `${generated.fileBaseName}.png`;
                    link.click();
                  }}
                >
                  下载预览图
                </button>
              </div>
            </div>
            <p>Word 文件使用可被 Word 打开的 `.doc` HTML 格式；预览图由同一份结构化内容绘制，便于微信或工作群快速确认版式。</p>
          </article>

          <article className="daily-report-card daily-report-output">
            <div className="daily-report-output-head">
              <h2>牛博士口吻的明日推送消息</h2>
              <div>
                <button type="button" onClick={() => copyText(generated.avoman)}>复制</button>
                <button type="button" onClick={submitDailyUpdate} disabled={isSubmittingDailyUpdate}>
                  {isSubmittingDailyUpdate ? '提交中...' : '提交为下次定时推送'}
                </button>
              </div>
            </div>
            <pre>{generated.avoman}</pre>
            <p className="daily-report-status">附带一句话：{attachmentCaption.trim() || DEFAULT_ATTACHMENT_CAPTION}</p>
            {submitMessage ? <p className="daily-report-status">{submitMessage}</p> : null}
            {submitError ? <p className="daily-report-error">{submitError}</p> : null}
          </article>
        </section>
      )}
    </main>
  );
}
